import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";

/**
 * Shared sub-page header (About, Loan Rates, Careers, Blogs, Policies, Apply…).
 * Fluid: logo + wordmark scale from 320px up; actions collapse to icons on
 * small screens; nothing is clipped or pushed off-canvas.
 */
export const PolicyHeader = ({ testId = "policy-header", helpLabel = "WhatsApp" }) => (
  <header
    data-testid={testId}
    className="sticky top-0 z-40 border-b border-[#D4AF37]/15 bg-[#04102B]/90 backdrop-blur-md"
  >
    <div className="max-w-7xl mx-auto gutter min-h-[72px] sm:min-h-[96px] md:min-h-[120px] py-3 flex items-center justify-between gap-3">
      <Link
        to="/"
        data-testid={`${testId}-home`}
        aria-label="Sahyog Finance & Noble Infinity — Home"
        className="flex items-center gap-3 sm:gap-4 md:gap-5 min-w-0"
      >
        <Logo
          size={52}
          className="sm:!w-[72px] sm:!h-[72px] md:!w-[92px] md:!h-[92px]"
          testId={`${testId}-logo`}
        />
        <div className="leading-tight min-w-0">
          <div className="font-display text-white text-[13px] sm:text-xl md:text-[26px] lg:text-3xl uppercase tracking-tight font-semibold leading-[1.15] whitespace-nowrap keep-nowrap">
            SAHYOG FINANCE
            <br />
            <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
          </div>
          <div className="font-display text-[#D4AF37] text-[8px] sm:text-[11px] md:text-sm tracking-[0.14em] sm:tracking-[0.16em] uppercase mt-1 whitespace-nowrap keep-nowrap">
            Asset Management Pvt. Ltd.
          </div>
        </div>
      </Link>
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <Link
          to="/"
          data-testid={`${testId}-back`}
          aria-label="Back to home"
          className="hidden min-[360px]:inline-flex items-center gap-1.5 text-white/70 hover:text-[#F0D788] text-xs uppercase tracking-[0.2em] min-h-[44px] min-w-[44px] justify-center px-2 rounded-full transition-colors"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          <span className="hidden md:inline">Home</span>
        </Link>
        <a
          href="https://wa.me/917016975324"
          target="_blank"
          rel="noreferrer"
          aria-label={helpLabel}
          className="inline-flex items-center justify-center gap-2 min-h-[44px] min-w-[44px] px-3 sm:px-4 rounded-full bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-sm text-emerald-200 transition-colors"
        >
          <MessageCircle size={16} aria-hidden="true" />
          <span className="hidden sm:inline">{helpLabel}</span>
        </a>
      </div>
    </div>
  </header>
);

export const PolicyPageShell = ({ children, eyebrow, title, lead, testId }) => (
  <div data-testid={testId} className="min-h-screen bg-[#04102B] text-white relative overflow-x-clip">
    <div className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#0F6B3D]/8 blur-3xl" />
    <PolicyHeader testId={`${testId}-header`} />
    <main id="main-content" tabIndex={-1} className="relative max-w-6xl mx-auto gutter section-pad">
      <div className="text-center mb-10 md:mb-14">
        <span className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37]">
          · {eyebrow} ·
        </span>
        <h1 className="font-display fluid-h1 mt-5 tracking-tight text-balance">
          {title}
        </h1>
        {lead && (
          <p className="mt-5 max-w-2xl mx-auto text-white/70 fluid-lead">{lead}</p>
        )}
      </div>
      {children}
    </main>
  </div>
);
